import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Row, Modal, Table, Button, Switch, Space, Col, Card, Tag } from "antd";
import TableBar from "components/TableBar";
import { parseQueryParams, stringifyQueryParams } from "utils/url";
import { TrashIcon, PencilAltIcon, LoginIcon } from "@heroicons/react/outline";
import PageTitle from "components/PageTitle";
import OrderViewCardOrders from "./OverviewCardOrders";
import OverviewCardPlan from "./OverviewCardPlan";
import { useSelector } from "react-redux";
import axios from "axios";
// requests
import {
  getSubBills,
  createUser,
  deleteBill,
  deleteSubBill,
  updateBill,
} from "requests/bill";
import moment from "moment";
import { removeCookie, setCookie } from "utils/cookie";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';

// import OverviewCardPlan from "routes/overviewPayin/OverviewCardPlan";

const BillSubCategoryByDate = () => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(
    process.env.REACT_APP_RECORDS_PER_PAGE
  );
  const [totalCount, setTotalCount] = useState(0);
  const [records, setRecords] = useState([]);
  const [visibleCreateForm, setVisibleCreateForm] = useState(false);
  const [visibleUpdateForm, setVisibleUpdateForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { category_id } = useParams();

  const location = useLocation();
  const navigate = useNavigate();

  const titles = [{ path: location.pathname, title: "Users" }];

  const UpdateStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 1 || currentStatus === "1" ? 0 : 1;

    try {
      const response = await axios.post(
        "https://billpay.mtmpay.in/Admin/api/admin/change-bill-subcategory-status",
        { id, status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.status === "1") {
        toast.success(response.data.message || "Status updated successfully");
        getRecords(); 
      } else {
        toast.error(response.data.message || "Failed to update status");
      }
    } catch (error) {
      toast.error("Something went wrong while updating status.");
      console.error(error);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name", // Matching the key from the response.
      key: "name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Switch
          checked={status === 1 || status === "1"}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
          onChange={() => UpdateStatus(record._id || record.id, status)}
        />
      ),
    },
    {
      title: "Actions",
      render: (text, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => onEdit(record)}>
            <PencilAltIcon width={24} height={24} />
          </Button>
          <Button
            danger
            type="link"
            size="small"
            onClick={() => onDelete(record._id)}
          >
            <TrashIcon width={24} height={24} />
          </Button>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    const query = parseQueryParams(location);
    getRecords(query);
  }, [location]);

  const onChangeTable = (pagination, filters, sorter, extra) => {
    console.log(pagination, filters, sorter, extra);

    let query = parseQueryParams(location);
    query = {
      ...query,
      page: pagination.current,
      per_page: pagination.pageSize,
    };

    if (sorter.order) {
      query = {
        ...query,
        order_by: sorter.field,
        order_type: sorter.order === "ascend" ? "asc" : "desc",
      };
    } else {
      delete query.order_by;
      delete query.order_type;
    }

    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams(query),
    });
  };

  const onSearch = (keyword) => {
    let query = parseQueryParams(location);
    query = {
      ...query,
      page: 1,
      keyword: keyword,
    };

    navigate({
      pathname: location.pathname,
      search: stringifyQueryParams(query),
    });
  };

  const getRecords = async (query) => {
    try {
      const updatedQuery = { ...query, status: 'all', category_id: category_id, };
      const response = await getSubBills(updatedQuery);
      const { data, current_page, per_page, total } = response;
  
      setRecords(data);
      setPage(current_page);
      setPerPage(per_page);
      setTotalCount(total);
    } catch (err) {
      console.error(err);
    }
  };
  
  const onToggleCreateForm = () => {
    setVisibleCreateForm(!visibleCreateForm);
  };

  const onCreate = async (data) => {
    try {
      await createUser(data);
      // refresh list
      navigate({
        pathname: location.pathname,
        search: stringifyQueryParams({}),
      });
    } catch (err) {
      console.log(err);
    }
  };

  const onEdit = (data) => {
    setSelectedUser(data);
    onToggleUpdateForm();
  };

  const onToggleUpdateForm = () => {
    // in case hide update form, set selected currency is null
    if (visibleUpdateForm) setSelectedUser(null);

    setVisibleUpdateForm(!visibleUpdateForm);
  };

  const onUpdate = async (data) => {
    try {
      await updateBill(selectedUser._id, data);

      // refresh list
      navigate({
        pathname: location.pathname,
        search: stringifyQueryParams({}),
      });
    } catch (err) {
      console.log(err);
    }
  };

  const onDelete = (id) => {
    Modal.confirm({
      title: "Warning",
      content: "Are you sure to delete this record?",
      onOk: async () => {
        await deleteSubBill(id);
        // refresh list
        navigate({
          pathname: location.pathname,
          search: stringifyQueryParams({}),
        });
      },
    });
  };


  const onRefresh = () => {
    const query = parseQueryParams(location);
    getRecords(query);
  };

  const onUpdateStatus = (id, status) => {
    Modal.confirm({
      title: `Are you sure to ${status ? "enable" : "disable"} this user?`,
      onOk: async () => {
        try {
          await updateBill(id, { status: status });

          onRefresh();
        } catch (err) {
          console.log(err);
        }
      },
    });
  };

  const disabledRowIds = [436, 438];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      ;
    },
    // getCheckboxProps: (record) => {
    //   return {
    //     disabled: disabledRowIds.includes(record.id),
    //   };
    // },
  };

  return (
    <div>
      <div className="overviewBorder">
        <Row gutter={[8, 8]} align={"middle"} justify={"space-between"}>
          {/* <PageTitle titles={titles} /> */}
          <Col xs={24} sm={24} md={12} lg={5} xl={5}>
          <h1 className="overviewTitle text-white">Bill Category</h1>
          </Col>
          <Col xs={24} sm={24} md={12} lg={6} xl={5}>
            <Card className="small_card">
              <Button
                type="primary"
                onClick={onToggleCreateForm}
                className="h-50"
              >
                Create new bill category
              </Button>
            </Card>
          </Col>
          <Col xs={24} sm={24} md={24} lg={6} xl={7}>
            <Card className="small_card">
              <TableBar showFilter={false} onSearch={onSearch} />
            </Card>
          </Col>
        </Row>
      </div>

      <Table
        // rowSelection={rowSelection}
        columns={columns}
        dataSource={records}
        rowKey={(record) => record.id || record._id}
        onChange={onChangeTable}
        pagination={{
          pageSize: perPage,
          total: totalCount,
          current: page,
        }}
      />
      <OrderViewCardOrders
        visible={visibleCreateForm}
        onClose={onToggleCreateForm}
        onSubmit={onCreate}
      />
      {selectedUser && (
        <OverviewCardPlan
          record={{ ...selectedUser, id: selectedUser._id}}
          visible={visibleUpdateForm}
          onClose={onToggleUpdateForm}
          onSubmit={onUpdate}
        />
      )}
      <ToastContainer />
    </div>

  );
};

export default BillSubCategoryByDate;
